import { Component, inject, OnInit, ViewChild, Input, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forEach } from 'lodash-es';
import { Router } from '@angular/router';
import { FilterPipe, OrderByPipe } from '@wm/components-base';
import { BaseComponent, WMSEditorComponent, SearchComponent, WmsTreeComponent, IWMSStandaloneEditorConstructionOptions } from '@wms/components';
import { getClonedObject, getCurrencyOptions, getFormatPatterns, getDateTimePatterns, getSecurityContextOptions, CURRENCYCONSTANTS } from '@wms/app/core';
import { BindVariableService, CustomExtensionService } from '@wms/services';
import { IPosition, Position } from "monaco-editor";
import { PageSelectComponent } from "@wms/app/components/bind-dialog/page-select/page-select.component";

@Component({
  selector: 'wms-bind-use-expression',
  standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        WMSEditorComponent,
        SearchComponent,
        WmsTreeComponent,
        FormsModule,
        OrderByPipe,
        FilterPipe,
        PageSelectComponent
    ],
  templateUrl: './bind-use-expression.component.html',
  styleUrls: ['./bind-use-expression.component.less']
})
export class BindUseExpressionComponent extends BaseComponent implements OnInit, AfterViewInit {

  @Input() isloaded: boolean = false;
  expr: any;
  defaults: any;
  expressionFunctions: any;
  pipesArr: any;
  formatOptions: any;
  customPipeOptions: any;
  dateTimePatterns: any;
  currencyOptions: any;
  securityContextOptions: any;
  searchKey: any;
  router: Router;
  updateEditorContentSubscription: any;
  bindVariableService: BindVariableService;
  customExtensionService: CustomExtensionService;
  useExpLink: any = "https://docs.wavemaker.com/learn/app-development/variables/variable-binding/#binding-using-expression";
  editorOptions: IWMSStandaloneEditorConstructionOptions = {
    value: "",
    language: "javascript",
    automaticLayout: true,
    renderLineHighlight: "none",
    wordWrap: "on",
    wrappingIndent: "indent",
    codeLens: false
  };
  @ViewChild("codeEditor") wmsEditorEl: WMSEditorComponent;

  constructor() {
    super();
    this.bindVariableService = inject(BindVariableService);
    this.customExtensionService = inject(CustomExtensionService);
    this.router = inject(Router);
  }

  filterContent($event: any) {
    this.searchKey = $event.searchKey;
  }

  resetExpr() {
    let fn = this.expr.fn;
    if (fn == 'custompipe') {
      return;
    }
    this.expr = {
      'fn': fn
    };
  }

  constructExpression() {
    if (!this.expr.fn) {
      return;
    }
    let customExpression = ' | ';
    // If expression not defind in the expressionFunctions then selected expresion is custom pipe
    if (this.expressionFunctions.indexOf(this.expr.fn) >= 0 || ['trustAs', 'sanitize'].indexOf(this.expr.fn) >= 0) {
      customExpression = customExpression + this.expr.fn;
    } else {
      customExpression = customExpression + 'custom';
      let custompipe = { custompipe: this.expr.fn };
      this.expr = Object.assign(custompipe, this.expr);
    }
    if (this.expr.fn === "toCurrency") {
      if (!this.expr.currency) {
        /*Setting the currency symbol if not yet set*/
        this.setCurrencySymbol();
      }
      customExpression = customExpression + ": '" + this.expr.currency + "'" + (this.expr.fracSize ? ": '" + this.expr.fracSize + "'" : "");
      this.expr.fn = undefined;
    } else if (this.expr.fn === "templateReplace") {
      customExpression = `${customExpression}: {"" : ""}` ;
      this.expr.fn = undefined;
    } else {
      this.expr.fn = undefined;
      forEach(this.expr, (value: any) => {
        if (value) {
          customExpression = customExpression + ": '" + value + "'";
        }
      });
    }
    this.bindVariableService.insertText(customExpression);
  }

  setCurrencySymbol() {
    this.expr.currency = CURRENCYCONSTANTS[this.defaults.currency] ? CURRENCYCONSTANTS[this.defaults.currency].symbol : this.defaults.currency;
  }

  setCustomPipeConfig(exp: any) {
    if (exp == 'custompipe') {
      let fileName = 'extensions/formatters.js',
        relativePath = '/src/main/webapp/' + fileName,
        resourcePath = '../project' + relativePath;

      this.router.navigate(['/files', {
        name: fileName,
        relativePath: relativePath,
        path: resourcePath,
        type: "file"
      }]);
      return;
    }
    if (!this.customPipeOptions || !this.customPipeOptions[exp] || !this.customPipeOptions[exp].config) {
      return;
    }
    for (let key of Object.keys(this.customPipeOptions[exp].config)) {
      this.expr[this.customPipeOptions[exp].config[key].name] = this.customPipeOptions[exp].config[key].value;
    }
  }

  onExprChange() {
    this.bindVariableService.useExprEditorContent = this.editorOptions.value = this.wmsEditorEl.options.value;
  }

  ngOnChanges() {
    if (this.isloaded) {
      this.setCursorPosition();
    }
  }

  setCursorPosition(newPosition?: any ) {
    setTimeout(() => {
      // set cursorPosition only if the editor has been loaded
      if (this.wmsEditorEl.wmsEditor) {
        this.wmsEditorEl.wmsEditor.focus();
        if (newPosition) {
          this.wmsEditorEl.setCursorPosition(newPosition);
        } else {
          const position: IPosition = { column: (this.editorOptions.value?.length || 0) + 1, lineNumber: this.wmsEditorEl.getLineCount() || 1 };
          this.wmsEditorEl.setCursorPosition(position);
        }
      }
    });
  }

  private insertTextAtCursor(textToInsert: string, cursorPosition: Position) {
    const currentContent = this.editorOptions.value || '';
    const lines = currentContent.split('\n');
    let offset = 0;

    for (let i = 0; i < cursorPosition.lineNumber - 1 && i < lines.length; i++) {
      offset += lines[i].length + 1;
    }
    offset += Math.max(0, cursorPosition.column - 1);

    return currentContent.slice(0, offset) + textToInsert + currentContent.slice(offset);
  }

  ngOnInit(): void {
    let currentTab = this.bindVariableService.currentTab,
      boundEntity = this.bindVariableService.dialogParams.boundEntity;
    if (this.bindVariableService.useExprEditorContent) {
      this.editorOptions = { ...this.editorOptions, value: this.bindVariableService.useExprEditorContent, language: "javascript" };
    }
    else if ((currentTab === 'widget' || currentTab === 'variable' || currentTab === 'expression') && boundEntity?.toString().startsWith("bind")) {
      let newValue = boundEntity.replace("bind:", '');
      this.editorOptions = { ...this.editorOptions, value: newValue, language: "javascript" };
    }
    if (!this.bindVariableService.displayExprTeeLoaded) {
      this.bindVariableService.defaultAction(false);
    }
    this.updateEditorContentSubscription = this.bindVariableService.updateEditorContentSubject.subscribe((response: any) => {
      const currentEditorContent: any = this.editorOptions.value;
      const position: Position | null = this.wmsEditorEl.getCursorPosition();
      let newEditorContent: any = currentEditorContent;
      if (response.update) {
        newEditorContent = position ? this.insertTextAtCursor(response.update, position) : currentEditorContent + response.update;
      } else if (response.set !== undefined) {
        newEditorContent = response.set;
      }
      this.editorOptions.value = newEditorContent;
      this.wmsEditorEl.setEditorValue(newEditorContent);
      this.bindVariableService.useExprEditorContent = newEditorContent;
      const newCursorPosition = response.update
        ? {
          lineNumber: position?.lineNumber || 1,
          column: (position?.column || 0) + response.update.length,
        }
        : undefined;
      this.setCursorPosition(newCursorPosition);
    });

    this.initExprVariables();
  }

  ngAfterViewInit() {
    this.wmsEditorEl.ensureMonacoEditorLoaded().then(() => {
      this.setCursorPosition();
    });
  }

  initExprVariables() {
    this.expr = {
      'fn': ''
    };
    this.defaults = {
      currency: 'USD',
      currencySymbol: '$'
    };
    this.formatOptions = getClonedObject(getFormatPatterns());
    this.formatOptions.pop();
    this.formatOptions.push(this.locale.LABEL_PROPERTY_TEMPLATEREPLACE);
    this.expressionFunctions = this.formatOptions;
    this.dateTimePatterns = getDateTimePatterns();
    this.currencyOptions = getCurrencyOptions();
    this.securityContextOptions = getSecurityContextOptions();
    if (!this.bindVariableService.isPrefabTemplate && !this.bindVariableService.isTemplateBundleType) {
      // To load the custom pipes from formatters file
      (this.customExtensionService.getCustomFormatters() as Promise<any>).then((response: any) => {
        this.customPipeOptions = response;
        this.pipesArr = Object.keys(response);
      });
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.updateEditorContentSubscription) {
      this.updateEditorContentSubscription.unsubscribe();
    }
  }

}
